import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { formatCurrency, currencySymbol } from '../../../utils/formatters';
import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { openProfileDrawer, openProfileDrawerToDealProposal } from './profileDrawerHelper';
import { SEND_NDA_TO_RECEIVING_PARTY } from '../../../graphql/mutations/dealMutations';
import { extractDealId } from '../../../utils/dealUtils';
import { MapPinIcon, UserCircleIcon, StarIcon, UsersIcon, BriefcaseIcon, CalendarIcon, CheckCircleIcon, BookmarkIcon, ChatBubbleLeftRightIcon, EnvelopeIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import { useSuccessionTheme } from '../SuccessionThemeProvider';

// Success Animation Component
const SuccessAnimation = ({ message, onComplete }) => {
  const [particles, setParticles] = useState([]);
  
  React.useEffect(() => {
    // Create more particles and make them bigger
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 0,
      rotation: Math.random() * 360,
      scale: Math.random() * 0.8 + 0.8, // Increased base size
      color: ['#3B82F6', '#10B981', '#6366F1', '#8B5CF6', '#EC4899'][Math.floor(Math.random() * 5)]
    }));
    
    setParticles(newParticles);
    
    // Cleanup after animation
    const timer = setTimeout(() => {
      onComplete();
    }, 2500); // Slightly longer duration
    
    return () => clearTimeout(timer);
  }, [onComplete]);
  
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {/* Success Message - Made bigger */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-succession-bg-primary rounded-xl shadow-succession-lg p-6 animate-slide-down min-w-[300px]">
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 rounded-full bg-succession-success-10 flex items-center justify-center">
            <CheckCircleIcon className="h-8 w-8 text-succession-success" />
          </div>
          <span className="text-xl font-semibold text-succession-text-primary">{message}</span>
        </div>
      </div>
      
      {/* Particles - Made bigger */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute w-4 h-4 rounded-full" // Increased size
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            backgroundColor: particle.color,
            transform: `rotate(${particle.rotation}deg) scale(${particle.scale})`,
            animation: `particle-fall 1.5s ease-out forwards`, // Slightly longer duration
            animationDelay: `${Math.random() * 0.5}s`,
            boxShadow: '0 0 10px rgba(255,255,255,0.5)' // Added glow effect
          }}
        />
      ))}
    </div>
  );
};

// Update the animation styles
const styles = `
  @keyframes particle-fall {
    0% {
      transform: translateY(0) rotate(0deg) scale(1);
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(720deg) scale(0); // More rotation
      opacity: 0;
    }
  }
  
  @keyframes slide-down {
    0% {
      transform: translate(-50%, -100%);
      opacity: 0;
    }
    10% {
      transform: translate(-50%, 0);
      opacity: 1;
    }
    90% {
      transform: translate(-50%, 0);
      opacity: 1;
    }
    100% {
      transform: translate(-50%, -100%);
      opacity: 0;
    }
  }
  
  .animate-slide-down {
    animation: slide-down 2.5s ease-in-out forwards;
  }
`;

// Add styles to document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

// Helper function to format numbers in a concise way
const formatNumber = (value) => {
  // Handle null, undefined, empty strings, or zero values
  if (value === null || value === undefined || value === '' || value === 0) {
    return '--';
  }
  
  // Parse the value to a number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Check if it's a valid number after parsing
  if (isNaN(numValue)) {
    return '--';
  }
  
  // Format based on magnitude
  const sym = currencySymbol();
  if (numValue >= 1000000) {
    return `${sym}${(numValue / 1000000).toFixed(1)}M`;
  } else if (numValue >= 1000) {
    return `${sym}${(numValue / 1000).toFixed(0)}K`;
  } else {
    return `${sym}${numValue.toFixed(0)}`;
  }
};

const metricIcons = {
  assets: (
    <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 8c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2" />
    </svg>
  ), // AUM
  revenue: (
    <svg className="h-5 w-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 17l6-6 4 4 7-7" />
    </svg>
  ), // Revenue
  clients: (
    <svg className="h-5 w-5 text-purple-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ), // Clients
  years: (
    <svg className="h-5 w-5 text-orange-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  ), // Years
};

const DealInterestCard = ({
  dealInterest,
  dealName,
  companySettings: companySettingsProp,
  onSave,
  onInvite,
  onRemoveFromShortlist,
  onSendNda,
  onViewDetails,
  viewDetailsPath,
  viewDetailsLabel,
  scheduleMeetingPath,
  badgeText,
  badgeColor,
  allowShortlistMessaging = false
}) => {
  const intl = useIntl();
  const [showSuccess, setShowSuccess] = useState(false);
  const { company: themeCompanySettings } = useSuccessionTheme();
  const companySettings = companySettingsProp || themeCompanySettings;
  const [successMessage, setSuccessMessage] = useState('');
  const resolvedViewDetailsLabel = viewDetailsLabel || intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.view_details' });
  
  const advisor = dealInterest?.inventory_user?.user;
  if (!advisor) return null;
  const avatarStyle = getAvatarStyle(advisor.profile_picture, companySettings);
  
  // Remove the internal SEND_NDA_TO_RECEIVING_PARTY mutation to avoid duplicates
  // The parent component (DealShortlisted) will handle the NDA sending
  
  // Function to handle sending NDA via parent callback
  const handleSendNda = () => {
    if (typeof onSendNda === 'function') {
      onSendNda(dealInterest.id);
    } else {
      console.error("No onSendNda callback provided");
      alert("Error: Unable to send NDA - no handler available");
    }
  };
  
  // Function to open messaging with this advisor
  const handleMessageClick = () => {
    if (advisor && advisor.user_name) {
      // Store the tab value (5 is the index for DealTab) in sessionStorage
      sessionStorage.setItem('profileDrawer_tab', '5');
      
      // Store the section to focus on
      sessionStorage.setItem('profileDrawer_focusOn', 'chat');
      
      // Open the profile drawer
      window.location.href = `/profiles/${advisor.user_name}`;
    }
  };
  
  // Function to open the user profile drawer
  const handleOpenProfile = () => {
    if (advisor && advisor.user_name) {
      openProfileDrawer(advisor.user_name, { fromDeal: true, dealInterest });
    }
  };

  // Function to handle view profile for details button
  const handleViewProfile = () => {
    if (advisor?.fb_user_name) {
      openProfileDrawer(advisor.fb_user_name, { fromDeal: true, dealInterest });
    }
  };
  
  // Determine advisor type (match, rolodex, follower)
  const getAdvisorType = () => {
    if (!dealInterest) return null;

    if (dealInterest.workflow_state === 'following') {
      return { type: 'rolodex', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.rolodex' }), classes: 'bg-succession-primary-10 text-succession-primary' };
    } else if (dealInterest.workflow_state === 'follower') {
      return { type: 'follower', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.follower' }), classes: 'bg-succession-accent-10 text-succession-accent' };
    } else if (dealInterest.reason_for_suggestion) {
      return { type: 'matches', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.suggested_match' }), classes: 'bg-succession-success-10 text-succession-success' };
    }

    return null;
  };

  // Determine workflow state (invited, shortlisted, etc.)
  const getWorkflowState = () => {
    if (!dealInterest) return null;

    if (dealInterest.workflow_state === 'invited') {
      return { type: 'invited', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.invited' }), classes: 'bg-succession-success-10 text-succession-success' };
    } else if (dealInterest.workflow_state === 'shortlisted') {
      return { type: 'shortlisted', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.shortlisted' }), classes: 'bg-succession-warning-10 text-succession-warning' };
    } else if (dealInterest.workflow_state === 'declined') {
      return { type: 'declined', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.declined' }), classes: 'bg-succession-error-bg text-succession-error' };
    } else if (dealInterest.workflow_state === 'reviewing_proposal') {
      return { type: 'reviewing', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.proposal_received' }), classes: 'bg-succession-primary-10 text-succession-primary' };
    } else if (dealInterest.workflow_state === 'out_for_receiving_signature') {
      return { type: 'nda_out', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.nda_out_for_review' }), classes: 'bg-succession-warning-10 text-succession-warning' };
    } else if (dealInterest.workflow_state === 'awaiting_disclosing_signature') {
      return { type: 'nda_awaiting', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.awaiting_your_signature' }), classes: 'bg-succession-accent-10 text-succession-accent' };
    } else if (dealInterest.workflow_state === 'nda_completed') {
      return { type: 'nda_completed', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.nda_completed' }), classes: 'bg-succession-success-10 text-succession-success' };
    } else if (dealInterest.workflow_state === 'negotiating') {
      return { type: 'negotiating', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.negotiating' }), classes: 'bg-succession-secondary-10 text-succession-secondary' };
    }

    return null;
  };
  
  const advisorType = getAdvisorType();
  const workflowState = getWorkflowState();
  
  // Get badge color class based on color name
  const getBadgeColorClasses = (color) => {
    switch (color) {
      case 'blue':
        return 'bg-succession-primary-10 text-succession-primary';
      case 'green':
        return 'bg-succession-success-10 text-succession-success';
      case 'red':
        return 'bg-succession-error-bg text-succession-error';
      case 'yellow':
        return 'bg-succession-warning-10 text-succession-warning';
      case 'purple':
        return 'bg-succession-secondary-10 text-succession-secondary';
      case 'orange':
        return 'bg-succession-accent-10 text-succession-accent';
      case 'gray':
      default:
        return 'bg-succession-bg-tertiary text-succession-text-muted';
    }
  };
  
  // Render status badges (allows for multiple badges)
  const renderStatusBadges = () => {
    const badges = [];
    
    // If custom badge is provided, use it as primary badge
    if (badgeText) {
      badges.push(
        <span 
          key="customBadge"
          className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium ${getBadgeColorClasses(badgeColor || 'gray')}`}
        >
          {badgeText}
        </span>
      );
    }
    // Otherwise if workflow state badge is available, use it as primary badge
    else if (workflowState) {
      badges.push(
        <span 
          key="workflowState"
          className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium ${workflowState.classes}`}
        >
          {workflowState.label}
        </span>
      );
    }
    
    // Always add advisor type badge if it exists and hasn't been shown yet
    if (advisorType && 
        !(badgeText && badgeText.includes(advisorType.label)) && 
        !(workflowState && workflowState.label === advisorType.label)) {
      badges.push(
        <span 
          key="advisorType"
          className={`ml-2 flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium ${advisorType.classes}`}
        >
          {advisorType.label}
        </span>
      );
    }
    
    // Add saved badge if item is saved
    if (dealInterest.saved) {
      badges.push(
        <span 
          key="saved"
          className="ml-2 flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-primary-light text-succession-primary"
        >
          Saved
        </span>
      );
    }
    
    return badges;
  };
  
  // Modified save handler
  const handleSave = (e) => {
    e.preventDefault();
    if (onSave) {
      onSave(dealInterest.id);
      setSuccessMessage(intl.formatMessage({ id: 'succession_deals.deal_interest_card.success.saved' }));
      setShowSuccess(true);
    }
  };

  // Modified invite handler
  const handleInvite = (e) => {
    e.preventDefault();
    if (onInvite) {
      onInvite(dealInterest.id);
      setSuccessMessage(intl.formatMessage({ id: 'succession_deals.deal_interest_card.success.invited' }));
      setShowSuccess(true);
    }
  };
  
  // Determine which actions should be available based on workflow state
  const renderActionButtons = () => {
    const actions = [];
    
    // Save button (available in matches, invited)
    if (onSave && !dealInterest.saved) {
      actions.push(
        <button
          key="save"
          onClick={handleSave}
          className="flex-1 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-300 hover:scale-105 hover:shadow-md active:scale-95"
        >
          <BookmarkIcon className="h-5 w-5 mr-1 inline-block" /> Save
        </button>
      );
    } else if (dealInterest.saved) {
      actions.push(
        <button
          key="saved"
          disabled
          className="flex-1 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-500 cursor-not-allowed"
        >
          Saved
        </button>
      );
    }
    
    // Invite button (available in matches) - use workflow_state as source of truth
    if (onInvite && dealInterest.workflow_state === 'matching') {
      actions.push(
        <button
          key="invite"
          onClick={handleInvite}
          className="flex-1 py-2 rounded-lg text-sm font-semibold bg-succession-primary text-white hover:bg-succession-primary-hover transition-all duration-300 hover:scale-105 hover:shadow-md active:scale-95"
        >
          <EnvelopeIcon className="h-5 w-5 mr-1 inline-block" /> Invite
        </button>
      );
    } else if (dealInterest.workflow_state === 'invited' && advisorType?.type === 'matches') {
      actions.push(
        <button
          key="invited"
          disabled
          className="flex-1 py-2 rounded-md text-sm font-medium bg-succession-primary-light text-succession-primary cursor-not-allowed"
        >
          Invited
        </button>
      );
    }
    
    // Remove from shortlist button
    if (onRemoveFromShortlist && dealInterest.workflow_state === 'shortlisted') {
      actions.push(
        <button
          key="remove"
          onClick={() => onRemoveFromShortlist(dealInterest.id)}
          className="flex-1 py-2 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
        >
          Remove
        </button>
      );
    }
    
    // Shortlisted state: Message and Send NDA buttons
    if (dealInterest.workflow_state === 'shortlisted') {
      // Message button (if messaging is allowed for shortlisted)
      if (allowShortlistMessaging) {
        actions.push(
          <button
            key="message"
            onClick={handleMessageClick}
            className="flex-1 py-2 rounded-md text-sm font-medium bg-succession-primary text-white hover:bg-succession-primary-hover flex items-center justify-center"
          >
            <span className="fa fa-envelope mr-2"></span>
            Message
          </button>
        );
      }
      
      // Send NDA button - only show if onSendNda callback is provided
      if (onSendNda) {
        actions.push(
          <button
            key="sendNda"
            onClick={handleSendNda}
            className="flex-1 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 flex items-center justify-center"
          >
            <span className="fas fa-file-signature mr-2"></span>
            Send NDA
          </button>
        );
      }
      
      // Return actions for shortlisted state
      if (actions.length > 0) {
        return (
          <div className="flex mt-4 space-x-3">
            {actions}
          </div>
        );
      }
    }
    
    // Read Expression of Interest button (for proposals in review)
    if (dealInterest.workflow_state === 'reviewing_proposal') {
      actions.push(
        <button
          key="readExpression"
          onClick={() => {
            if (dealInterest.inventory_user?.user?.user_name) {
              openProfileDrawerToDealProposal(dealInterest.inventory_user.user.user_name, dealInterest);
            }
          }}
          className="flex-1 py-2 rounded-md text-sm font-medium bg-succession-primary text-white hover:bg-succession-primary-hover flex items-center justify-center"
        >
          <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          Read Expression of Interest
        </button>
      );
      return actions; // Return early to only show this button for this state
    }
    
    // Schedule Meeting button/link
    if (scheduleMeetingPath) {
      let path = scheduleMeetingPath;
      if (typeof scheduleMeetingPath === 'function') {
        path = scheduleMeetingPath(dealInterest.id);
      }
      
      actions.push(
        <Link
          key="scheduleMeeting"
          to={path}
          className="flex-1 py-2 rounded-md text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 text-center"
        >
          Schedule Meeting
        </Link>
      );
    }
    
    // View details button/link
    if (viewDetailsPath) {
      let path = viewDetailsPath;
      if (typeof viewDetailsPath === 'function') {
        path = viewDetailsPath(dealInterest.id);
      }
      
      actions.push(
        <button
          key="viewDetails"
          onClick={() => {
            if (advisor?.fb_user_name) {
              openProfileDrawer(advisor.fb_user_name, { fromDeal: true, dealInterest });
            }
          }}
          className="flex-1 py-2 rounded-lg text-sm font-semibold bg-succession-primary text-white hover:bg-succession-primary-hover text-center transition-colors duration-150 shadow-md flex items-center justify-center"
        >
          <DocumentTextIcon className="h-5 w-5 mr-1 inline-block" /> {resolvedViewDetailsLabel}
        </button>
      );
    } else if (onViewDetails) {
      actions.push(
        <button
          key="viewDetails"
          onClick={() => {
            if (advisor?.fb_user_name) {
              openProfileDrawer(advisor.fb_user_name, { fromDeal: true, dealInterest });
            }
          }}
          className="flex-1 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 text-center transition-colors duration-150 shadow-md flex items-center justify-center"
        >
          <DocumentTextIcon className="h-5 w-5 mr-1 inline-block" /> {resolvedViewDetailsLabel}
        </button>
      );
    }
    
    return (
      <div className="flex mt-4 space-x-3">
        {actions}
      </div>
    );
  };
  
  const isPastInvitedStage = dealInterest.workflow_state && !['matching', 'invited'].includes(dealInterest.workflow_state);
  
  return (
    <>
      {showSuccess && (
        <SuccessAnimation 
          message={successMessage} 
          onComplete={() => setShowSuccess(false)} 
        />
      )}
      
      <div className="relative bg-succession-bg-primary/80 backdrop-blur-lg border border-succession-border shadow-xl rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:border-succession-primary group">
        {/* Animated border accent */}
        <div className="absolute inset-0 pointer-events-none rounded-2xl border-2 border-transparent group-hover:border-succession-primary transition-all duration-300 z-10" />
        <div className="p-6 flex flex-col gap-4">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div
              className="relative h-16 w-16 rounded-full shadow-lg flex items-center justify-center overflow-hidden border-4 border-succession-bg-primary group-hover:scale-105 transition-transform duration-300"
              style={{ backgroundColor: avatarStyle.backgroundColor }}
            >
              {advisor.profile_picture ? (
                <img 
                  src={avatarStyle.url}
                  alt={advisor.allowed_name}
                  className="h-full w-full object-cover"
                  style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                />
              ) : (
                <UserCircleIcon className="h-14 w-14 text-blue-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 
                className="text-xl font-bold text-succession-text-primary truncate cursor-pointer hover:underline hover:text-succession-primary transition-colors duration-200 mb-0.5"
                onClick={() => handleViewProfile()}
              >
                {advisor.headline || advisor.allowed_name}
              </h2>
              <div className="flex items-center gap-1 text-sm text-gray-500 mt-[1px]">
                <MapPinIcon className="h-4 w-4 text-blue-400 mr-1" />
                {advisor.city ? `${advisor.city}${advisor.province ? `, ${advisor.province}` : ''}` : intl.formatMessage({ id: 'succession_deals.deal_interest_card.location_unknown' })}
              </div>
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mt-2">
                {/* Render status badges here (unchanged logic, but with new styles) */}
                {badgeText && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-200 to-blue-400 text-blue-900 shadow-sm`}>
                    {badgeText}
                  </span>
                )}
                {/* Add more badges as needed, e.g. workflowState, advisorType, Saved */}
                {dealInterest.saved && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-succession-primary-light text-succession-primary shadow-sm">
                    <BookmarkIcon className="h-4 w-4 mr-1" /> {intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.saved' })}
                  </span>
                )}
                {dealInterest.workflow_state === 'following' && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 shadow-sm">
                    {intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.rolodex' })}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-succession-primary-light/60 rounded-xl p-4 mt-2 mb-1 animate-fade-in">
            <div className="flex flex-col items-center">
              {metricIcons.assets}
              <span className="text-xs text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'succession_deals.deal_interest_card.metric.aum' })}</span>
              <span className="text-base font-semibold text-succession-text-primary">{formatNumber(advisor.assets_under_management)}</span>
            </div>
            <div className="flex flex-col items-center">
              {metricIcons.revenue}
              <span className="text-xs text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'succession_deals.deal_interest_card.metric.revenue' })}</span>
              <span className="text-base font-semibold text-succession-text-primary">{formatNumber(advisor.annual_revenue)}</span>
            </div>
            <div className="flex flex-col items-center">
              {metricIcons.clients}
              <span className="text-xs text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'succession_deals.deal_interest_card.metric.clients' })}</span>
              <span className="text-base font-semibold text-succession-text-primary">{advisor.number_of_clients !== undefined ? advisor.number_of_clients : '--'}</span>
            </div>
            <div className="flex flex-col items-center">
              {metricIcons.years}
              <span className="text-xs text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'succession_deals.deal_interest_card.metric.years' })}</span>
              <span className="text-base font-semibold text-succession-text-primary">{advisor.years_practicing !== undefined ? advisor.years_practicing : '--'}</span>
            </div>
          </div>

          {/* Reason for Match */}
          {dealInterest.reason_for_suggestion && (
            <div className="flex items-start gap-2 bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg p-3 text-blue-800 text-sm font-medium shadow-inner animate-fade-in">
              <CheckCircleIcon className="h-5 w-5 text-blue-400 mt-0.5" />
              <span><strong>{intl.formatMessage({ id: 'succession_deals.deal_interest_card.reason_for_match' })}</strong> {dealInterest.reason_for_suggestion.length > 150
                ? `${dealInterest.reason_for_suggestion.substring(0, 150)}...` 
                : dealInterest.reason_for_suggestion
              }</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3 mt-2">
            {/* Save/Invite/Other actions (unchanged logic, but with new button styles) */}
            <div className="flex gap-2">
              {onSave && !dealInterest.saved && (
                <button
                  onClick={handleSave}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all duration-300 hover:scale-105 hover:shadow-md active:scale-95"
                >
                  <BookmarkIcon className="h-5 w-5 mr-1 inline-block" /> {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.save' })}
                </button>
              )}
              {dealInterest.saved && !isPastInvitedStage && (
                <button
                  disabled
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-succession-bg-light text-succession-text-muted cursor-not-allowed shadow-sm"
                >
                  <BookmarkIcon className="h-5 w-5 mr-1 inline-block" /> {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.saved' })}
                </button>
              )}
              {onInvite && dealInterest.workflow_state === 'matching' && (
                <button
                  onClick={handleInvite}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-succession-primary text-white hover:bg-succession-primary-hover transition-all duration-300 hover:scale-105 hover:shadow-md active:scale-95"
                >
                  <EnvelopeIcon className="h-5 w-5 mr-1 inline-block" /> {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.invite' })}
                </button>
              )}
              {dealInterest.workflow_state === 'invited' && (
                <button
                  disabled
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-succession-primary-light text-succession-primary cursor-not-allowed shadow-sm"
                >
                  <EnvelopeIcon className="h-5 w-5 mr-1 inline-block" /> {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.invited' })}
                </button>
              )}
            </div>
            {/* Schedule Meeting, View Details, etc. */}
            <div className="flex gap-2">
              {scheduleMeetingPath && (
                <Link
                  to={typeof scheduleMeetingPath === 'function' ? scheduleMeetingPath(dealInterest.id) : scheduleMeetingPath}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 text-center transition-colors duration-150 shadow-md flex items-center justify-center"
                >
                  <CalendarIcon className="h-5 w-5 mr-1 inline-block" /> {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.schedule_meeting' })}
                </Link>
              )}
              {viewDetailsPath && (
                <button
                  onClick={() => {
                    if (advisor?.fb_user_name) {
                      openProfileDrawer(advisor.fb_user_name, { fromDeal: true, dealInterest });
                    }
                  }}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-succession-primary text-white hover:bg-succession-primary-hover text-center transition-colors duration-150 shadow-md flex items-center justify-center"
                >
                  <DocumentTextIcon className="h-5 w-5 mr-1 inline-block" /> {resolvedViewDetailsLabel}
                </button>
              )}
              {!viewDetailsPath && onViewDetails && (
                <button
                  onClick={() => {
                    if (advisor?.fb_user_name) {
                      openProfileDrawer(advisor.fb_user_name, { fromDeal: true, dealInterest });
                    }
                  }}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-succession-primary text-white hover:bg-succession-primary-hover text-center transition-colors duration-150 shadow-md flex items-center justify-center"
                >
                  <DocumentTextIcon className="h-5 w-5 mr-1 inline-block" /> {resolvedViewDetailsLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

DealInterestCard.defaultProps = {
  viewDetailsLabel: null,
};

DealInterestCard.propTypes = {
  dealInterest: PropTypes.object.isRequired,
  dealName: PropTypes.string.isRequired,
  companySettings: PropTypes.shape({
    avatar_background_color: PropTypes.string,
    avatar_directory: PropTypes.string,
    primary_color: PropTypes.string,
  }),
  onSave: PropTypes.func,
  onInvite: PropTypes.func,
  onRemoveFromShortlist: PropTypes.func,
  onSendNda: PropTypes.func,
  onViewDetails: PropTypes.func,
  viewDetailsPath: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  viewDetailsLabel: PropTypes.string,
  scheduleMeetingPath: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  badgeText: PropTypes.string,
  badgeColor: PropTypes.string,
  allowShortlistMessaging: PropTypes.bool
};

export default DealInterestCard; 